import {
	ChangeEvent,
	FC,
	SyntheticEvent,
	useEffect,
	useMemo,
	useState,
} from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PreviewIcon from "@mui/icons-material/Preview";
import SearchIcon from "@mui/icons-material/Search";
import {
	ActionRequest,
	ActionUpdateRequest,
	useCreateAction,
	useDeleteAction,
	useGetActions,
	useGetAllActions,
	useUpdateAction,
} from "../../hooks/useActions";
import {
	ActionGroupRequest,
	ActionGroupUpdateRequest,
	useCreateActionGroup,
	useDeleteActionGroup,
	useGetActionGroups,
	useGetAllActionGroups,
	useGetRolesPreview,
	useReorderActionGroups,
	useUpdateActionGroup,
} from "../../hooks/useActionGroups";
import {
	IAction,
	resolveActionGroupId,
	resolveActionGroupName,
} from "../../domain/action.interface";
import {
	ActionGroupStatus,
	IActionGroup,
	RolesPreviewSection,
} from "../../domain/action-group.interface";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import { useUserStore } from "../../stores/user.store";
import { apiErrorMessage } from "../../utils/apiErrorMessage";

const ACTION_TYPES = [
	"MENU_OPTION",
	"KPI",
	"ALERTS",
	"OPTION",
	"EVENTS",
	"SPECIAL_FEATURE",
	"REPORT",
	"System",
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, { label: "All", value: 1000 }];

type ManagementTab = "actions" | "groups";

type ActionFormErrors = Partial<
	Record<"code" | "name" | "description" | "type" | "groupId" | "order", string>
>;

type GroupFormErrors = Partial<Record<"name", string>>;

const createEmptyActionForm = (createdBy: string): ActionRequest => ({
	code: "",
	name: "",
	description: "",
	type: "MENU_OPTION",
	groupId: "",
	link: "",
	order: 0,
	icon: "",
	subActions: [],
	createdBy,
});

const createEmptyGroupForm = (createdBy: string): ActionGroupRequest => ({
	name: "",
	description: "",
	icon: "",
	status: "ACTIVE",
	createdBy,
});

const ActionsComponent: FC = () => {
	const { userInfo } = useUserStore();
	const { showSnackBar } = useFeedBack();
	const actorId = userInfo?.id || "SYSTEM_USER";

	const [activeTab, setActiveTab] = useState<ManagementTab>("actions");
	const [actionSearchInput, setActionSearchInput] = useState("");
	const [actionSearch, setActionSearch] = useState("");
	const [actionType, setActionType] = useState("");
	const [actionPage, setActionPage] = useState(0);
	const [actionPageSize, setActionPageSize] = useState(10);
	const [showActionForm, setShowActionForm] = useState(false);
	const [editingAction, setEditingAction] = useState<IAction | null>(null);
	const [actionForm, setActionForm] = useState<ActionRequest>(
		createEmptyActionForm(actorId),
	);
	const [actionFormErrors, setActionFormErrors] = useState<ActionFormErrors>(
		{},
	);

	const [groupSearchInput, setGroupSearchInput] = useState("");
	const [groupSearch, setGroupSearch] = useState("");
	const [groupPage, setGroupPage] = useState(0);
	const [groupPageSize, setGroupPageSize] = useState(10);
	const [showGroupForm, setShowGroupForm] = useState(false);
	const [editingGroup, setEditingGroup] = useState<IActionGroup | null>(null);
	const [groupForm, setGroupForm] = useState<ActionGroupRequest>(
		createEmptyGroupForm(actorId),
	);
	const [groupFormErrors, setGroupFormErrors] = useState<GroupFormErrors>({});
	const [groupDraft, setGroupDraft] = useState<IActionGroup[]>([]);
	const [hasUnsavedOrder, setHasUnsavedOrder] = useState(false);
	const [rolesPreviewOpen, setRolesPreviewOpen] = useState(false);

	const actionsQuery = useGetActions(
		actionSearch || undefined,
		actionPage + 1,
		actionPageSize,
		actionType || undefined,
	);
	const allActionsQuery = useGetAllActions();
	const groupsQuery = useGetActionGroups(
		groupSearch || undefined,
		groupPage + 1,
		groupPageSize,
	);
	const allGroupsQuery = useGetAllActionGroups();
	const rolesPreviewQuery = useGetRolesPreview(rolesPreviewOpen);

	const createActionMutation = useCreateAction();
	const updateActionMutation = useUpdateAction();
	const deleteActionMutation = useDeleteAction();
	const createGroupMutation = useCreateActionGroup();
	const updateGroupMutation = useUpdateActionGroup();
	const deleteGroupMutation = useDeleteActionGroup();
	const reorderGroupsMutation = useReorderActionGroups();

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setActionSearch(actionSearchInput);
			setActionPage(0);
		}, 300);
		return () => clearTimeout(timeoutId);
	}, [actionSearchInput]);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setGroupSearch(groupSearchInput);
			setGroupPage(0);
			setHasUnsavedOrder(false);
		}, 300);
		return () => clearTimeout(timeoutId);
	}, [groupSearchInput]);

	const actions = useMemo(
		() => actionsQuery.data?.body?.data || [],
		[actionsQuery.data],
	);
	const groups = useMemo(
		() => groupsQuery.data?.body?.data || [],
		[groupsQuery.data],
	);
	const allGroups = useMemo(
		() => allGroupsQuery.data?.body?.data || [],
		[allGroupsQuery.data],
	);
	const activeGroups = useMemo(
		() => allGroups.filter((group) => group.status === "ACTIVE"),
		[allGroups],
	);
	const groupsById = useMemo(
		() => new Map(allGroups.map((group) => [group._id, group])),
		[allGroups],
	);

	useEffect(() => {
		if (!hasUnsavedOrder) {
			setGroupDraft(groups);
		}
	}, [groups, hasUnsavedOrder]);

	const editingActionId = editingAction?._id;
	const { selectedSubActions, subActionOptions } = useMemo(() => {
		const allActions = allActionsQuery.data?.body?.data || [];
		const actionsById = new Map(
			allActions.map((action) => [action._id, action]),
		);
		const selected = (actionForm.subActions || [])
			.map((id) => actionsById.get(id))
			.filter((action): action is IAction => Boolean(action));
		// FR-17: only MENU_OPTION actions can be selected as sub-actions.
		const pickable = allActions.filter(
			(action) =>
				action.type === "MENU_OPTION" && action._id !== editingActionId,
		);
		return {
			selectedSubActions: selected,
			subActionOptions: [
				...pickable,
				...selected.filter(
					(action) => !pickable.some((option) => option._id === action._id),
				),
			],
		};
	}, [actionForm.subActions, allActionsQuery.data, editingActionId]);

	const selectedActionGroup =
		activeGroups.find((group) => group._id === actionForm.groupId) || null;
	const isRecognizedActionType = ACTION_TYPES.includes(actionForm.type);
	const actionTotalCount = actionsQuery.data?.body?.pagination?.totalCount || 0;
	const groupTotalCount = groupsQuery.data?.body?.pagination?.totalCount || 0;

	const previewSections = useMemo<RolesPreviewSection[]>(() => {
		const sections = rolesPreviewQuery.data || [];
		if (sections.some((section) => section.isUngrouped)) {
			return sections;
		}
		return [
			...sections,
			{
				groupId: null,
				name: "Ungrouped",
				order: Number.MAX_SAFE_INTEGER,
				isUngrouped: true,
				actions: [],
			},
		];
	}, [rolesPreviewQuery.data]);

	const clearActionFieldError = (field: keyof ActionFormErrors): void => {
		setActionFormErrors((currentErrors) => {
			if (!currentErrors[field]) return currentErrors;
			const nextErrors = { ...currentErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	const validateActionForm = (): boolean => {
		const errors: ActionFormErrors = {};
		if (!actionForm.code.trim()) errors.code = "Code is required";
		if (!actionForm.name.trim()) errors.name = "Name is required";
		if (!actionForm.description.trim()) {
			errors.description = "Description is required";
		}
		if (!actionForm.type.trim()) errors.type = "Type is required";
		if (!activeGroups.some((group) => group._id === actionForm.groupId)) {
			errors.groupId = "An active group is required";
		}
		if (
			actionForm.type === "MENU_OPTION" &&
			(!Number.isInteger(actionForm.order) || actionForm.order < 0)
		) {
			errors.order = "Order must be a non-negative integer";
		}
		setActionFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const closeActionForm = (): void => {
		setShowActionForm(false);
		setEditingAction(null);
		setActionFormErrors({});
		setActionForm(createEmptyActionForm(actorId));
	};

	const openCreateActionForm = (): void => {
		setEditingAction(null);
		setActionFormErrors({});
		setActionForm(createEmptyActionForm(actorId));
		setShowActionForm(true);
	};

	const openEditActionForm = (action: IAction): void => {
		setEditingAction(action);
		setActionFormErrors({});
		setActionForm({
			code: action.code,
			name: action.name,
			description: action.description,
			type: action.type,
			groupId: resolveActionGroupId(action.groupId) || "",
			link: action.link || "",
			order: action.order,
			icon: action.icon || "",
			subActions: ((action.subActions || []) as Array<string | IAction>).map(
				(subAction) =>
					typeof subAction === "string" ? subAction : subAction._id,
			),
			createdBy: actorId,
		});
		setShowActionForm(true);
	};

	const saveAction = async (): Promise<void> => {
		if (!validateActionForm()) return;
		try {
			if (editingAction) {
				const request: ActionUpdateRequest = {
					...actionForm,
					_id: editingAction._id,
					updatedBy: actorId,
				};
				await updateActionMutation.mutateAsync(request);
				showSnackBar("Action updated successfully", "success");
			} else {
				await createActionMutation.mutateAsync(actionForm);
				showSnackBar("Action created successfully", "success");
			}
			closeActionForm();
		} catch (error) {
			showSnackBar(
				apiErrorMessage(
					error,
					editingAction ? "Failed to update action" : "Failed to create action",
				),
				"error",
			);
		}
	};

	const deleteAction = async (action: IAction): Promise<void> => {
		if (!window.confirm(`Are you sure you want to delete "${action.name}"?`)) {
			return;
		}
		try {
			await deleteActionMutation.mutateAsync(action._id);
			showSnackBar("Action deleted successfully", "success");
		} catch (error) {
			showSnackBar(
				apiErrorMessage(
					error,
					"Failed to delete action. It may still be assigned to roles.",
				),
				"error",
			);
		}
	};

	const validateGroupForm = (): boolean => {
		const errors: GroupFormErrors = {};
		if (!groupForm.name.trim()) errors.name = "Name is required";
		setGroupFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const closeGroupForm = (): void => {
		setShowGroupForm(false);
		setEditingGroup(null);
		setGroupFormErrors({});
		setGroupForm(createEmptyGroupForm(actorId));
	};

	const openCreateGroupForm = (): void => {
		setEditingGroup(null);
		setGroupFormErrors({});
		setGroupForm(createEmptyGroupForm(actorId));
		setShowGroupForm(true);
	};

	const openEditGroupForm = (group: IActionGroup): void => {
		setEditingGroup(group);
		setGroupFormErrors({});
		setGroupForm({
			name: group.name,
			description: group.description || "",
			icon: group.icon || "",
			status: group.status,
			createdBy: actorId,
		});
		setShowGroupForm(true);
	};

	const saveGroup = async (): Promise<void> => {
		if (!validateGroupForm()) return;
		try {
			if (editingGroup) {
				const request: ActionGroupUpdateRequest = {
					_id: editingGroup._id,
					name: groupForm.name,
					description: groupForm.description,
					icon: groupForm.icon,
					status: groupForm.status,
					updatedBy: actorId,
				};
				await updateGroupMutation.mutateAsync(request);
				showSnackBar("Group updated successfully", "success");
			} else {
				await createGroupMutation.mutateAsync(groupForm);
				showSnackBar("Group created successfully", "success");
			}
			closeGroupForm();
		} catch (error) {
			showSnackBar(
				apiErrorMessage(
					error,
					editingGroup ? "Failed to update group" : "Failed to create group",
				),
				"error",
			);
		}
	};

	const deleteGroup = async (group: IActionGroup): Promise<void> => {
		if (!window.confirm(`Are you sure you want to delete "${group.name}"?`)) {
			return;
		}
		try {
			await deleteGroupMutation.mutateAsync(group._id);
			showSnackBar("Group deleted successfully", "success");
		} catch (error) {
			showSnackBar(
				apiErrorMessage(
					error,
					"Failed to delete group. Move its actions to another group first.",
				),
				"error",
			);
		}
	};

	const moveGroup = (index: number, direction: -1 | 1): void => {
		const targetIndex = index + direction;
		if (targetIndex < 0 || targetIndex >= groupDraft.length) return;

		const reordered = [...groupDraft];
		[reordered[index], reordered[targetIndex]] = [
			reordered[targetIndex],
			reordered[index],
		];
		const orderSlots = groupDraft
			.map((group) => group.order)
			.sort((first, second) => first - second);
		setGroupDraft(
			reordered.map((group, position) => ({
				...group,
				order: orderSlots[position],
			})),
		);
		setHasUnsavedOrder(true);
	};

	const saveGroupOrder = async (): Promise<void> => {
		try {
			const draftOrderById = new Map(
				groupDraft.map((group) => [group._id, group.order]),
			);
			const orderedGroups = allGroups.length > 0 ? allGroups : groupDraft;
			await reorderGroupsMutation.mutateAsync({
				items: orderedGroups.map((group) => ({
					groupId: group._id,
					order: draftOrderById.get(group._id) ?? group.order,
				})),
				updatedBy: actorId,
			});
			setHasUnsavedOrder(false);
			showSnackBar("Group order saved successfully", "success");
		} catch (error) {
			showSnackBar(
				apiErrorMessage(error, "Failed to save group order"),
				"error",
			);
		}
	};

	const handleTabChange = (
		_event: SyntheticEvent,
		value: ManagementTab,
	): void => {
		setActiveTab(value);
	};

	const renderActionForm = (): JSX.Element | null => {
		if (!showActionForm) return null;

		return (
			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						{editingAction ? "Edit Action" : "Create Action"}
					</Typography>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								required
								disabled={Boolean(editingAction)}
								label="Code"
								value={actionForm.code}
								error={Boolean(actionFormErrors.code)}
								helperText={actionFormErrors.code}
								onChange={(event: ChangeEvent<HTMLInputElement>) => {
									setActionForm({ ...actionForm, code: event.target.value });
									clearActionFieldError("code");
								}}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								required
								label="Name"
								value={actionForm.name}
								error={Boolean(actionFormErrors.name)}
								helperText={actionFormErrors.name}
								onChange={(event: ChangeEvent<HTMLInputElement>) => {
									setActionForm({ ...actionForm, name: event.target.value });
									clearActionFieldError("name");
								}}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								fullWidth
								required
								multiline
								rows={2}
								label="Description"
								value={actionForm.description}
								error={Boolean(actionFormErrors.description)}
								helperText={actionFormErrors.description}
								onChange={(event: ChangeEvent<HTMLInputElement>) => {
									setActionForm({
										...actionForm,
										description: event.target.value,
									});
									clearActionFieldError("description");
								}}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							{editingAction && !isRecognizedActionType ? (
								<TextField
									fullWidth
									required
									label="Type"
									value={actionForm.type}
									error={Boolean(actionFormErrors.type)}
									helperText={
										actionFormErrors.type ||
										"This existing type is not in the standard list."
									}
									onChange={(event: ChangeEvent<HTMLInputElement>) => {
										setActionForm({
											...actionForm,
											type: event.target.value,
										});
										clearActionFieldError("type");
									}}
								/>
							) : (
								<FormControl
									fullWidth
									required
									error={Boolean(actionFormErrors.type)}
								>
									<InputLabel id="action-type-label">Type</InputLabel>
									<Select
										labelId="action-type-label"
										label="Type"
										value={actionForm.type}
										onChange={(event: SelectChangeEvent) => {
											setActionForm({
												...actionForm,
												type: event.target.value,
											});
											clearActionFieldError("type");
										}}
									>
										{ACTION_TYPES.map((type) => (
											<MenuItem key={type} value={type}>
												{type}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)}
						</Grid>
						<Grid item xs={12} md={6}>
							<Autocomplete
								options={activeGroups}
								value={selectedActionGroup}
								getOptionLabel={(group) => group.name}
								isOptionEqualToValue={(option, value) =>
									option._id === value._id
								}
								onChange={(_event, group) => {
									setActionForm({
										...actionForm,
										groupId: group?._id || "",
									});
									clearActionFieldError("groupId");
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										required
										label="Group"
										error={Boolean(actionFormErrors.groupId)}
										helperText={actionFormErrors.groupId}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Link"
								value={actionForm.link}
								onChange={(event: ChangeEvent<HTMLInputElement>) =>
									setActionForm({
										...actionForm,
										link: event.target.value,
									})
								}
							/>
						</Grid>
						{actionForm.type === "MENU_OPTION" && (
							<>
								<Grid item xs={12} md={3}>
									<TextField
										fullWidth
										label="Order"
										type="number"
										value={
											Number.isNaN(actionForm.order) ? "" : actionForm.order
										}
										error={Boolean(actionFormErrors.order)}
										helperText={actionFormErrors.order}
										inputProps={{ min: 0 }}
										onChange={(event: ChangeEvent<HTMLInputElement>) => {
											const order = Number.parseInt(event.target.value, 10);
											setActionForm({
												...actionForm,
												order: Number.isNaN(order) ? NaN : order,
											});
											clearActionFieldError("order");
										}}
									/>
								</Grid>
								<Grid item xs={12} md={3}>
									<TextField
										fullWidth
										label="Icon"
										value={actionForm.icon}
										onChange={(event: ChangeEvent<HTMLInputElement>) =>
											setActionForm({
												...actionForm,
												icon: event.target.value,
											})
										}
									/>
								</Grid>
							</>
						)}
						<Grid item xs={12}>
							<Autocomplete
								multiple
								options={subActionOptions}
								value={selectedSubActions}
								getOptionLabel={(action) => `${action.name} (${action.code})`}
								isOptionEqualToValue={(option, value) =>
									option._id === value._id
								}
								onChange={(_event, selected) =>
									setActionForm({
										...actionForm,
										subActions: selected.map((action) => action._id),
									})
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Sub-actions"
										placeholder="Select child actions"
									/>
								)}
							/>
						</Grid>
					</Grid>
					<Box mt={2} display="flex" gap={2}>
						<Button
							variant="contained"
							onClick={saveAction}
							disabled={
								createActionMutation.isPending || updateActionMutation.isPending
							}
						>
							{editingAction ? "Update Action" : "Create Action"}
						</Button>
						<Button variant="outlined" onClick={closeActionForm}>
							Cancel
						</Button>
					</Box>
				</CardContent>
			</Card>
		);
	};

	const renderActionsTab = (): JSX.Element => (
		<Box>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mb={2}
			>
				<Typography variant="h5">Actions</Typography>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					disabled={activeGroups.length === 0}
					onClick={openCreateActionForm}
				>
					Add Action
				</Button>
			</Box>
			{activeGroups.length === 0 && !allGroupsQuery.isLoading && (
				<Alert
					severity="warning"
					sx={{ mb: 2 }}
					action={
						<Button
							color="inherit"
							size="small"
							onClick={() => setActiveTab("groups")}
						>
							Open Groups
						</Button>
					}
				>
					Create an active group before adding actions.
				</Alert>
			)}
			<Stack spacing={2} mb={3}>
				<TextField
					fullWidth
					placeholder="Search actions by name, description, or code..."
					value={actionSearchInput}
					onChange={(event: ChangeEvent<HTMLInputElement>) =>
						setActionSearchInput(event.target.value)
					}
					InputProps={{
						startAdornment: (
							<SearchIcon sx={{ mr: 1, color: "action.active" }} />
						),
					}}
				/>
				<Stack direction="row" gap={1} flexWrap="wrap">
					<Chip
						label="All"
						color={actionType === "" ? "primary" : "default"}
						onClick={() => {
							setActionType("");
							setActionPage(0);
						}}
					/>
					{ACTION_TYPES.map((type) => (
						<Chip
							key={type}
							label={type}
							color={actionType === type ? "primary" : "default"}
							onClick={() => {
								setActionType(type);
								setActionPage(0);
							}}
						/>
					))}
				</Stack>
			</Stack>
			{renderActionForm()}
			<Paper>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Code</TableCell>
								<TableCell>Name</TableCell>
								<TableCell>Group</TableCell>
								<TableCell>Type</TableCell>
								<TableCell>Order</TableCell>
								<TableCell>Sub-actions</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{actionsQuery.isLoading && (
								<TableRow>
									<TableCell colSpan={7} align="center">
										Loading actions...
									</TableCell>
								</TableRow>
							)}
							{actionsQuery.isError && (
								<TableRow>
									<TableCell colSpan={7}>
										<Alert severity="error">Failed to load actions.</Alert>
									</TableCell>
								</TableRow>
							)}
							{!actionsQuery.isLoading &&
								!actionsQuery.isError &&
								actions.length === 0 && (
									<TableRow>
										<TableCell colSpan={7} align="center">
											No actions found.
										</TableCell>
									</TableRow>
								)}
							{actions.map((action) => {
								const groupId = resolveActionGroupId(action.groupId);
								const groupName =
									resolveActionGroupName(action.groupId) ||
									(groupId ? groupsById.get(groupId)?.name : undefined) ||
									"Ungrouped";
								return (
									<TableRow key={action._id}>
										<TableCell>
											<Chip label={action.code} size="small" />
										</TableCell>
										<TableCell>{action.name}</TableCell>
										<TableCell>{groupName}</TableCell>
										<TableCell>
											<Chip
												label={action.type}
												size="small"
												color={
													action.type === "MENU_OPTION" ? "primary" : "default"
												}
											/>
										</TableCell>
										<TableCell>
											{action.type === "MENU_OPTION" ? action.order : "—"}
										</TableCell>
										<TableCell>
											{action.subActions?.length
												? `${action.subActions.length} sub-actions`
												: "—"}
										</TableCell>
										<TableCell align="right">
											<IconButton
												size="small"
												color="primary"
												aria-label={`Edit ${action.name}`}
												onClick={() => openEditActionForm(action)}
											>
												<EditIcon />
											</IconButton>
											<IconButton
												size="small"
												color="error"
												aria-label={`Delete ${action.name}`}
												disabled={deleteActionMutation.isPending}
												onClick={() => deleteAction(action)}
											>
												<DeleteIcon />
											</IconButton>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					rowsPerPageOptions={PAGE_SIZE_OPTIONS}
					component="div"
					count={actionTotalCount}
					rowsPerPage={actionPageSize}
					page={actionPage}
					onPageChange={(_event, newPage: number) => setActionPage(newPage)}
					onRowsPerPageChange={(event: ChangeEvent<HTMLInputElement>) => {
						setActionPageSize(Number.parseInt(event.target.value, 10));
						setActionPage(0);
					}}
				/>
			</Paper>
		</Box>
	);

	const renderGroupForm = (): JSX.Element | null => {
		if (!showGroupForm) return null;

		return (
			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						{editingGroup ? "Edit Group" : "Create Group"}
					</Typography>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								required
								label="Name"
								value={groupForm.name}
								error={Boolean(groupFormErrors.name)}
								helperText={groupFormErrors.name}
								onChange={(event: ChangeEvent<HTMLInputElement>) => {
									setGroupForm({ ...groupForm, name: event.target.value });
									setGroupFormErrors({});
								}}
							/>
						</Grid>
						<Grid item xs={12} md={3}>
							<TextField
								fullWidth
								label="Icon"
								value={groupForm.icon}
								onChange={(event: ChangeEvent<HTMLInputElement>) =>
									setGroupForm({
										...groupForm,
										icon: event.target.value,
									})
								}
							/>
						</Grid>
						<Grid item xs={12} md={3}>
							<FormControl fullWidth>
								<InputLabel id="group-status-label">Status</InputLabel>
								<Select
									labelId="group-status-label"
									label="Status"
									value={groupForm.status || "ACTIVE"}
									onChange={(event: SelectChangeEvent) =>
										setGroupForm({
											...groupForm,
											status: event.target.value as ActionGroupStatus,
										})
									}
								>
									<MenuItem value="ACTIVE">ACTIVE</MenuItem>
									<MenuItem value="INACTIVE">INACTIVE</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12}>
							<TextField
								fullWidth
								multiline
								rows={2}
								label="Description"
								value={groupForm.description}
								onChange={(event: ChangeEvent<HTMLInputElement>) =>
									setGroupForm({
										...groupForm,
										description: event.target.value,
									})
								}
							/>
						</Grid>
					</Grid>
					<Box mt={2} display="flex" gap={2}>
						<Button
							variant="contained"
							onClick={saveGroup}
							disabled={
								createGroupMutation.isPending || updateGroupMutation.isPending
							}
						>
							{editingGroup ? "Update Group" : "Create Group"}
						</Button>
						<Button variant="outlined" onClick={closeGroupForm}>
							Cancel
						</Button>
					</Box>
				</CardContent>
			</Card>
		);
	};

	const renderGroupsTab = (): JSX.Element => (
		<Box>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				gap={2}
				mb={2}
			>
				<Typography variant="h5">Groups</Typography>
				<Stack direction="row" spacing={1}>
					<Button
						variant="outlined"
						startIcon={<PreviewIcon />}
						onClick={() => setRolesPreviewOpen(true)}
					>
						Roles preview
					</Button>
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={openCreateGroupForm}
					>
						Add Group
					</Button>
				</Stack>
			</Box>
			<TextField
				fullWidth
				sx={{ mb: 3 }}
				placeholder="Search groups by name or description..."
				value={groupSearchInput}
				onChange={(event: ChangeEvent<HTMLInputElement>) =>
					setGroupSearchInput(event.target.value)
				}
				InputProps={{
					startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />,
				}}
			/>
			{renderGroupForm()}
			{hasUnsavedOrder && (
				<Alert
					severity="info"
					sx={{ mb: 2 }}
					action={
						<Button
							color="inherit"
							size="small"
							disabled={reorderGroupsMutation.isPending}
							onClick={saveGroupOrder}
						>
							Save order
						</Button>
					}
				>
					Unsaved order
				</Alert>
			)}
			<Paper>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Order</TableCell>
								<TableCell>Name</TableCell>
								<TableCell>Icon</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Description</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{groupsQuery.isLoading && (
								<TableRow>
									<TableCell colSpan={6} align="center">
										Loading groups...
									</TableCell>
								</TableRow>
							)}
							{groupsQuery.isError && (
								<TableRow>
									<TableCell colSpan={6}>
										<Alert severity="error">Failed to load groups.</Alert>
									</TableCell>
								</TableRow>
							)}
							{!groupsQuery.isLoading &&
								!groupsQuery.isError &&
								groupDraft.length === 0 && (
									<TableRow>
										<TableCell colSpan={6} align="center">
											No groups found.
										</TableCell>
									</TableRow>
								)}
							{groupDraft.map((group, index) => (
								<TableRow key={group._id}>
									<TableCell>
										<Stack direction="row" alignItems="center" spacing={0.5}>
											<Typography variant="body2">{group.order}</Typography>
											<IconButton
												size="small"
												disabled={index === 0}
												aria-label={`Move ${group.name} up`}
												onClick={() => moveGroup(index, -1)}
											>
												<ArrowUpwardIcon fontSize="small" />
											</IconButton>
											<IconButton
												size="small"
												disabled={index === groupDraft.length - 1}
												aria-label={`Move ${group.name} down`}
												onClick={() => moveGroup(index, 1)}
											>
												<ArrowDownwardIcon fontSize="small" />
											</IconButton>
										</Stack>
									</TableCell>
									<TableCell>{group.name}</TableCell>
									<TableCell>{group.icon || "—"}</TableCell>
									<TableCell>
										<Chip
											label={group.status}
											size="small"
											color={group.status === "ACTIVE" ? "success" : "default"}
										/>
									</TableCell>
									<TableCell>{group.description || "—"}</TableCell>
									<TableCell align="right">
										<IconButton
											size="small"
											color="primary"
											aria-label={`Edit ${group.name}`}
											onClick={() => openEditGroupForm(group)}
										>
											<EditIcon />
										</IconButton>
										<IconButton
											size="small"
											color="error"
											aria-label={`Delete ${group.name}`}
											disabled={deleteGroupMutation.isPending}
											onClick={() => deleteGroup(group)}
										>
											<DeleteIcon />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					rowsPerPageOptions={PAGE_SIZE_OPTIONS}
					component="div"
					count={groupTotalCount}
					rowsPerPage={groupPageSize}
					page={groupPage}
					onPageChange={(_event, newPage: number) => {
						setHasUnsavedOrder(false);
						setGroupPage(newPage);
					}}
					onRowsPerPageChange={(event: ChangeEvent<HTMLInputElement>) => {
						setHasUnsavedOrder(false);
						setGroupPageSize(Number.parseInt(event.target.value, 10));
						setGroupPage(0);
					}}
				/>
			</Paper>
		</Box>
	);

	return (
		<Box>
			<Typography variant="h4" mb={2}>
				Actions Management
			</Typography>
			<Tabs
				value={activeTab}
				onChange={handleTabChange}
				aria-label="Actions management sections"
				sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
			>
				<Tab value="actions" label="Actions" />
				<Tab value="groups" label="Groups" />
			</Tabs>
			{activeTab === "actions" ? renderActionsTab() : renderGroupsTab()}
			<Dialog
				open={rolesPreviewOpen}
				onClose={() => setRolesPreviewOpen(false)}
				fullWidth
				maxWidth="md"
			>
				<DialogTitle>Roles preview</DialogTitle>
				<DialogContent dividers>
					{rolesPreviewQuery.isLoading && (
						<Typography>Loading roles preview...</Typography>
					)}
					{rolesPreviewQuery.isError && (
						<Alert severity="error">Failed to load roles preview.</Alert>
					)}
					{!rolesPreviewQuery.isLoading && !rolesPreviewQuery.isError && (
						<Stack spacing={2}>
							{previewSections.map((section) => (
								<Paper
									key={section.groupId || "ungrouped"}
									variant="outlined"
									sx={{ p: 2 }}
								>
									<Stack
										direction="row"
										justifyContent="space-between"
										alignItems="center"
										mb={1}
									>
										<Typography variant="h6">{section.name}</Typography>
										{section.status && (
											<Chip label={section.status} size="small" />
										)}
									</Stack>
									{section.actions.length === 0 ? (
										<Typography color="text.secondary">No actions</Typography>
									) : (
										<Stack direction="row" gap={1} flexWrap="wrap">
											{section.actions.map((action) => (
												<Chip
													key={action._id}
													label={`${action.name} (${action.code})`}
													variant="outlined"
												/>
											))}
										</Stack>
									)}
								</Paper>
							))}
						</Stack>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRolesPreviewOpen(false)}>Close</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default ActionsComponent;
